import React, { useCallback } from "react"
import clsx from "clsx"
import { useDispatch } from "react-redux"
import { Grid, IconButton, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles, createStyles } from "@material-ui/core/styles"
import { faTimes } from "@fortawesome/pro-solid-svg-icons"
import { IUserSurveyCard, IUserSurveyCardColors, IUserSurveyDetails } from "shared/Modules/UserCards/userCardsTypes"
import SurveyCardBackground from "shared/Modules/Surveys/Assets/smiley_survey_bg.svg"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { hideUserCard } from "../../userCardsActions"
import AssetHelper from "shared/Helpers/AssetHelper"
import Card from "shared/Components/Card/Card"
import { useDateTime } from "shared/Modules/Localization/useDateTime"
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons"

type IClassesProps = {
    userSurvey?: IUserSurveyCardColors
}

const useStyles = makeStyles((theme) =>
    createStyles({
        userSurveyCardContent: ({ userSurvey }: IClassesProps) => ({
            backgroundColor: userSurvey?.background || theme.palette.common.white,
            padding: "1rem",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
        }),
        userSurveyCloseButton: ({ userSurvey }: IClassesProps) => ({
            color: userSurvey?.closeIcon || theme.palette.primary.main,
            height: theme.spacing(2.5),
            width: theme.spacing(2.5),
        }),
        userSurveyProductName: ({ userSurvey }: IClassesProps) => ({
            color: userSurvey?.productName || theme.palette.text.primary,
            height: "4em",
            lineHeight: 1.2,
            display: "-webkit-box",
            overflow: "hidden",
            textOverflow: "ellipsis",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
        }),
        userSurveyCardBg: {
            backgroundImage: `url(${AssetHelper.url(SurveyCardBackground)})`,
            backgroundPosition: `bottom right`,
            backgroundRepeat: "no-repeat",
            backgroundOrigin: "content-box",
            backgroundSize: 60,
            backgroundBlendMode: "color-burn",
        },
        userSurveyProductBlock: {
            flex: "1 1 auto",
            margin: "0.5rem 0",
            maxWidth: "70%",
        },

        userSurveyRateButtonBlock: ({ userSurvey }: IClassesProps) => ({
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            color: userSurvey?.action || theme.palette.primary.main,
            filter: "opacity(0.8)",
        }),
        cardHeading: {
            position: "absolute",
            right: "1rem",
            top: "1rem",
        },
        deliveryTime: ({ userSurvey }: IClassesProps) => ({
            color: userSurvey?.header || theme.palette.primary.main,
        }),
    })
)

type UserSurveyCardProps = Readonly<{
    card: IUserSurveyCard
    showSurveyModal: (survey: IUserSurveyDetails["content"]) => void
}>

export function UserSurveyCard({ card: { id, surveyDetails }, showSurveyModal }: UserSurveyCardProps) {
    const { content, colors } = surveyDetails
    const { product } = content
    const dispatch = useDispatch()
    const dateTimeFactory = useDateTime()

    const classes = useStyles({
        userSurvey: colors ?? {},
    })

    function handleSurveyDismiss(event: React.MouseEvent<HTMLButtonElement>) {
        event.stopPropagation()
        dispatch(hideUserCard(id))
    }

    const handleSurveyOpen = useCallback(() => showSurveyModal(content), [showSurveyModal, content])

    return (
        <>
            <Card boxShadow="none" width="100%" onClick={handleSurveyOpen}>
                <div className={clsx(classes.userSurveyCardContent, classes.userSurveyCardBg)}>
                    <div className={classes.cardHeading}>
                        <IconButton
                            aria-label="close"
                            size="small"
                            className={classes.userSurveyCloseButton}
                            onClick={handleSurveyDismiss}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                    </div>
                    <Typography variant="body1" className={classes.deliveryTime}>
                        {dateTimeFactory.formatDate(dateTimeFactory.fromFNDateTime(content.orderDeliveryTime))}
                    </Typography>
                    <Grid item container wrap="nowrap" alignItems="baseline" className={classes.userSurveyProductBlock}>
                        <Grid item>
                            <Typography variant="body2" className={classes.userSurveyProductName}>
                                {product.name}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item className={classes.userSurveyRateButtonBlock}>
                        <LocalizedStrict id="home-user-card-survey-rate-now">
                            <Typography variant="caption">Rate now</Typography>
                        </LocalizedStrict>
                        <FontAwesomeIcon icon={faArrowRight} size="xs" />
                    </Grid>
                </div>
            </Card>
        </>
    )
}
